import * as React from 'react'

import { t, tn, tp } from '@owl-nest/localize'
import { searchParams } from '@owl-nest/utils'
import * as api from '@owl-nest/api-client/latest'
import * as env from '@owl-nest/config'

import { userDrawerContext } from './userDrawerContext'
import { type SessionData, type Translations, type URLs } from '../../types'

type UserDrawerProviderProps = {
  lang: api.Lang
  children: React.ReactNode
}

export function UserDrawerProvider({
  lang,
  children,
}: UserDrawerProviderProps): React.ReactElement<UserDrawerProviderProps> {
  const sessionData = useSessionData(lang)
  return (
    <userDrawerContext.Provider value={{ sessionData, translations: getTranslations(), urls: getURLs(lang) }}>
      {children}
    </userDrawerContext.Provider>
  )
}

function formatURL(lang: api.Lang, url: string): string {
  const params: Record<string, boolean | number | string> = { lang }
  if (typeof window !== 'undefined') {
    params.next = window.location.href
  }
  return searchParams.addSearchParamsToUrl(url, params)
}

function useSessionData(lang: api.Lang): SessionData {
  const config = env.useUserEnv()
  return {
    currentCountry: env.USER_COUNTRY,
    currentCurrency: config.currency,
    currentLocale: lang,
    defaultLocale: env.DEFAULT_LOCALE,
    rates: env.CURRENCIES_RATES,
  }
}

function getTranslations(): Translations['userDrawer'] {
  return {
    contributionsBoxTitle: (contributionCount = 0) =>
      tn('My latest contribution', 'My latest contributions', contributionCount),
    myContributions: tp('My contributions', 'User menu'),
    myMessages: t('My messages'),
    myProfile: t('My profile'),
    myProjects: tp('My projects', 'User menu'),
    noProjectName: t('No title'),
    projectComingSoon: {
      validatedProject: t('Ready to go online'),
      notValidatedProject: t('In progress'),
    },
    projectsBoxTitle: (projectCount) => tn('My project', 'My projects', projectCount),
    seeMore: t('See more'),
    settings: t('Settings'),
    signIn: t('Sign in'),
    signOut: t('Sign out'),
    i18nSettingsLabel: t('Language and site currency'),
  }
}

function getURLs(lang: api.Lang): URLs {
  return {
    channels: `${env.ULULE_ORIGINS[lang]}${env.CHANNELS_URL}`,
    discover: `${env.ULULE_ORIGINS[lang]}${env.DISCOVER_URL}`,
    discoverLandingPage: `${env.ULULE_ORIGINS[lang]}${env.DISCOVER_LANDING_URL}`,
    home: `${env.ULULE_ORIGINS[lang]}${env.HOMEPAGE_URL}`,
    inbox: `${env.ULULE_ORIGINS[lang]}${env.DISCUSSIONS_URL}`,
    proposal: `${env.ULULE_ORIGINS[lang]}${env.PROPOSAL_URL}`,
    signIn: {
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()
        window.location.href = formatURL(lang, `${env.ULULE_ORIGINS[lang]}${env.SIGNIN_URL}`)
      },
      url: `${env.ULULE_ORIGINS[lang]}${env.SIGNIN_URL}`,
    },
    signOut: formatURL(lang, `${env.ULULE_ORIGINS[lang]}${env.SIGNOUT_URL}`),
  }
}
